// Custom, in-brand Privacy Policy — replaces the Iubenda-hosted page.
// DRAFT compiled from the project's actual data flows (Stripe, Resend,
// Cloudflare R2, MongoDB Atlas, Vercel, Railway, PostHog, Iubenda).
// ⚠️ Have this reviewed by counsel before relying on it in production.
import { Link } from "react-router-dom";

const UPDATED = "24 June 2026";

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2
        className="text-2xl md:text-3xl metal-gold-text mb-4"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {title}
      </h2>
      <div className="space-y-3 text-sm md:text-[15px] leading-relaxed text-[#A1A1A1]">
        {children}
      </div>
    </section>
  );
}

function Row({ a, b }) {
  return (
    <tr className="border-b border-[#1A1A1A] align-top">
      <td className="py-3 pr-4 text-[#EAEAEA] w-1/3">{a}</td>
      <td className="py-3 text-[#A1A1A1]">{b}</td>
    </tr>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen pt-28 md:pt-36 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <p className="text-[10px] tracking-[0.4em] uppercase metal-gold-text mb-3">
          Legal
        </p>
        <h1
          className="text-4xl md:text-5xl text-[#EAEAEA] mb-2"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Privacy Policy
        </h1>
        <p className="text-xs text-[#737373] mb-12">Last updated: {UPDATED}</p>

        <Section title="Who we are">
          <p>
            This site, <span className="text-gold">sanctumsonics.com</span> (“Sanctum
            Sonics”), is operated by <strong className="text-[#EAEAEA]">La Grotta
            Records</strong> (the “Data Controller”). For any privacy matter you can
            reach us at{" "}
            <a className="text-gold hover:underline" href="mailto:privacy@sanctumsonics.com">
              privacy@sanctumsonics.com
            </a>
            .
          </p>
          <p>
            We are committed to processing personal data lawfully under the EU General
            Data Protection Regulation (GDPR, Reg. 2016/679) and Italian Legislative
            Decree 196/2003 as amended.
          </p>
        </Section>

        <Section title="What we collect & why">
          <table className="w-full text-sm border-collapse">
            <tbody>
              <Row a="Email address" b="To process your order, deliver download links and the licence PDF, send order confirmations, and handle Forge (custom-beat) requests. Legal basis: performance of a contract." />
              <Row a="Order & licence data" b="Beat purchased, licence tier, amount, timestamps. Kept to deliver your files and for accounting/tax obligations. Legal basis: contract + legal obligation." />
              <Row a="Payment data" b="Card and billing details are entered on Stripe's hosted checkout and processed by Stripe — we never see or store your card number. We only receive a payment confirmation and the billing email." />
              <Row a="Forge brief content" b="The description you submit for a custom beat. Legal basis: contract (to fulfil your request)." />
              <Row a="Usage analytics" b="Only after you consent (cookie banner, purpose “Measurement”): anonymous/aggregate product analytics via PostHog, with all form inputs masked. Legal basis: consent." />
              <Row a="Technical logs" b="Standard server logs (IP, request metadata) generated by our hosting providers for security and reliability. Legal basis: legitimate interest." />
            </tbody>
          </table>
        </Section>

        <Section title="Service providers (processors)">
          <p>We share data only with providers that help us run the service, under appropriate agreements:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-[#EAEAEA]">Stripe</strong> — payment processing.</li>
            <li><strong className="text-[#EAEAEA]">Resend</strong> — transactional email delivery.</li>
            <li><strong className="text-[#EAEAEA]">Cloudflare R2</strong> — storage and delivery of audio files and licence PDFs.</li>
            <li><strong className="text-[#EAEAEA]">MongoDB Atlas</strong> — database hosting.</li>
            <li><strong className="text-[#EAEAEA]">Vercel</strong> & <strong className="text-[#EAEAEA]">Railway</strong> — website and API hosting.</li>
            <li><strong className="text-[#EAEAEA]">Iubenda</strong> — cookie-consent management.</li>
            <li><strong className="text-[#EAEAEA]">PostHog</strong> — product analytics (only with your consent).</li>
          </ul>
        </Section>

        <Section title="International transfers">
          <p>
            Some providers may process data outside the EEA (e.g. in the United States).
            Where that happens, transfers are covered by appropriate safeguards such as the
            EU Standard Contractual Clauses and, where applicable, the EU–US Data Privacy
            Framework.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>
            Order, licence and invoicing records are retained for the period required by
            Italian tax and accounting law (generally 10 years). Forge briefs are kept for
            the duration of the engagement. Analytics data is retained per our analytics
            provider's configuration. We delete or anonymise personal data once it is no
            longer needed for these purposes, or on a valid erasure request.
          </p>
        </Section>

        <Section title="Your rights">
          <p>Under the GDPR you may request to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>access the personal data we hold about you;</li>
            <li>rectify inaccurate data;</li>
            <li>erase your data (“right to be forgotten”);</li>
            <li>restrict or object to processing;</li>
            <li>receive your data in a portable format;</li>
            <li>withdraw consent at any time (without affecting prior processing).</li>
          </ul>
          <p>
            To exercise any right, email{" "}
            <a className="text-gold hover:underline" href="mailto:privacy@sanctumsonics.com?subject=GDPR%20request">
              privacy@sanctumsonics.com
            </a>
            . You also have the right to lodge a complaint with the Italian Data Protection
            Authority (Garante per la protezione dei dati personali).
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            We use a minimal set of cookies and similar technologies. Details and your
            controls are in our{" "}
            <Link className="text-gold hover:underline" to="/cookie-policy">
              Cookie Policy
            </Link>
            . You can change your choices any time via the “Manage Cookies” link in the
            footer.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this policy from time to time. The “last updated” date above
            reflects the latest revision.
          </p>
        </Section>

        <div className="mt-14 pt-8 border-t border-[#1A1A1A]">
          <Link to="/" className="text-[10px] tracking-[0.3em] uppercase metal-gold-text hover:brightness-125">
            ← Back to Sanctum Sonics
          </Link>
        </div>
      </div>
    </div>
  );
}
